import React from 'react';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { GetServerSideProps } from 'next';

import { RibbonsPage } from 'src/components/templates/RibbonsPage';
import { getPageContent } from 'src/content/getPageContent';
import {
  HOMEPAGE_WITHOUT_PLACEMENT,
  useHomepageRichRelevanceData,
} from 'src/hooks/useHomepageRichRelevanceData';
import { ContentPageProps, PageConfiguration } from 'src/types/PageProps';
import { getPageMetadata } from 'src/utils/getPageMetadata';
import { getRichRelevanceSessionId } from 'src/utils/getRichRelevanceSessionId';
import { SKIP_CONTENT_TARGET_ID } from '../constants/elementIds';

const pageConfig: PageConfiguration = {
  env: process.env.NODE_ENV,
  contentType: 'homepage',
  pageType: 'homepage',
  trackingCategory: 'Home',
  hasSeoLinkedData: true,
};

export const getServerSideProps: GetServerSideProps<ContentPageProps> = async (context) => {
  const pageMetadata = {
    ...getPageMetadata(context),
    functionalCookiesConsented: acceptsFunctionalCookies(context.req.cookies),
    richRelevanceSessionId: getRichRelevanceSessionId(context),
  };

  try {
    const pageContent = await getPageContent(pageConfig, pageMetadata);

    return {
      props: {
        pageConfig,
        pageMetadata,
        pageContent: pageContent.pageContent,
        category: pageConfig.trackingCategory, // is used for tracking the page
        navData: pageContent.navData,
        topBannerData: pageContent.topBannerData,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

const Homepage = (props: ContentPageProps) => {
  const {
    pageContent: { blocks },
    pageMetadata: { richRelevanceSessionId, functionalCookiesConsented },
  } = props;
  const hasRichRelevanceContentBlock = Boolean(
    blocks?.find(({ type }) => type === 'rich_relevance'),
  );
  useHomepageRichRelevanceData({
    placementType: hasRichRelevanceContentBlock ? null : HOMEPAGE_WITHOUT_PLACEMENT,
    sessionId: richRelevanceSessionId,
    hasDisabledCookies: !functionalCookiesConsented,
  });

  return (
    <div id={SKIP_CONTENT_TARGET_ID}>
      <RibbonsPage {...props} />;
    </div>
  );
};

export default Homepage;
